'use client'

import Loading from '@/components/circle-loading'
import { useEffect, useRef } from 'react'

/** Loads one bounded batch as the end enters the viewport or its scroll panel. */
export default function ScrollLoadMore({
	hasMore,
	loading = false,
	itemCount,
	onLoadMore,
	label = 'Loading more items…'
}: {
	hasMore: boolean
	loading?: boolean
	itemCount: number
	onLoadMore: () => void
	label?: string
}) {
	const sentinel = useRef<HTMLDivElement>(null)
	const loadMore = useRef(onLoadMore)
	useEffect(() => {
		loadMore.current = onLoadMore
	}, [onLoadMore])
	useEffect(() => {
		const node = sentinel.current
		if (!node || !hasMore || loading) return
		let requested = false
		const observer = new IntersectionObserver(
			(entries) => {
				if (requested || !entries.some((entry) => entry.isIntersecting)) return
				requested = true
				observer.disconnect()
				loadMore.current()
			},
			{ rootMargin: '200px 0px' }
		)
		observer.observe(node)
		return () => observer.disconnect()
	}, [hasMore, loading, itemCount])
	if (!hasMore && !loading) return null
	return (
		<div
			ref={sentinel}
			data-scroll-load-more
			className="flex min-h-10 items-center justify-center py-3 text-xs text-muted-foreground"
			role="status"
			aria-label={loading ? label : undefined}
		>
			{loading ? <Loading className="!w-6 !h-6" /> : null}
		</div>
	)
}
